import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const theme = {
  primary: "#5B4AE4",
  primaryLight: "#EDE9FF",
  primaryDark: "#4435B8",
  text: "#1A1A2E",
  textSecondary: "#6B7280",
  border: "#E5E7EB",
  bg: "#F8F7FC",
  white: "#FFFFFF",
  success: "#22C55E",
  requested: "#5B4AE4",
  requestedBg: "#EDE9FF",
  tagGreen: "#DCFCE7",
  tagGreenText: "#15803D",
};

const fonts = {
  sans: "'DM Sans', 'Segoe UI', sans-serif",
};

// ─── Inline MUI-like Components ───────────────────────────────

function Box({ sx = {}, children, ...rest }: { sx?: React.CSSProperties; children?: React.ReactNode }) {
  return (
    <div style={{ boxSizing: "border-box", ...sx }} {...rest}>
      {children}
    </div>
  );
}

type Variant = 'h5' | 'h6' | 'subtitle1' | 'subtitle2' | 'body1' | 'body2' | 'caption' | 'overline';

function Typography({ variant = "body1", sx = {}, children, component, ...rest }: { variant?: Variant; sx?: React.CSSProperties; children?: React.ReactNode; component?: React.ElementType; [key: string]: any }) {
  const variants = {
    h5: { fontSize: 22, fontWeight: 700, lineHeight: 1.3, margin: 0 },
    h6: { fontSize: 18, fontWeight: 700, lineHeight: 1.4, margin: 0 },
    subtitle1: { fontSize: 14, fontWeight: 600, lineHeight: 1.5, margin: 0 },
    subtitle2: { fontSize: 12, fontWeight: 600, lineHeight: 1.5, margin: 0, textTransform: "uppercase", letterSpacing: "0.06em" },
    body1: { fontSize: 14, fontWeight: 400, lineHeight: 1.6, margin: 0 },
    body2: { fontSize: 13, fontWeight: 400, lineHeight: 1.5, margin: 0 },
    caption: { fontSize: 12, fontWeight: 400, lineHeight: 1.5, margin: 0 },
    overline: { fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", margin: 0 },
  };
  const Tag = component || "p";
  return (
    <Tag style={{ fontFamily: fonts.sans, ...variants[variant], ...sx }} {...rest}>
      {children}
    </Tag>
  );
}

function Paper({ sx = {}, elevation = 1, children, ...rest }: { sx?: React.CSSProperties; elevation?: number; children?: React.ReactNode; [key: string]: any }) {
  const shadows: Record<number, string> = {
    0: "none",
    1: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
    2: "0 4px 12px rgba(0,0,0,0.08)",
    3: "0 8px 24px rgba(0,0,0,0.1)",
  };
  return (
    <div
      style={{
        background: theme.white,
        borderRadius: 14,
        border: `1px solid ${theme.border}`,
        boxShadow: shadows[elevation] || shadows[1],
        boxSizing: "border-box",
        ...sx,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

function Chip({ label, color = "default", sx = {} }: { label: string; color?: "default" | "success" | "primary"; sx?: React.CSSProperties }) {
  const colors = {
    default: { bg: theme.border, text: theme.textSecondary },
    success: { bg: theme.tagGreen, text: theme.tagGreenText },
    primary: { bg: theme.primaryLight, text: theme.primary },
  };
  const c = colors[color] || colors.default;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
        fontFamily: fonts.sans,
        background: c.bg,
        color: c.text,
        ...sx,
      }}
    >
      {label}
    </span>
  );
}

function Button({ variant = "contained", startIcon, children, sx = {}, ...rest }: { variant?: string; startIcon?: any; children?: React.ReactNode; sx?: any; } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: variant === "text" ? "6px 12px" : "12px 24px",
    borderRadius: 10,
    border: variant === "outlined" ? `1.5px solid ${theme.border}` : "none",
    background: variant === "contained" ? theme.primary : variant === "outlined" ? theme.white : "transparent",
    color: variant === "contained" ? "#fff" : theme.text,
    fontSize: 14,
    fontWeight: 600,
    fontFamily: fonts.sans,
    cursor: "pointer",
    transition: "all 0.2s ease",
    ...sx,
  };
  return (
    <button style={base} {...rest}>
      {startIcon && <span style={{ display: "flex", alignItems: "center" }}>{startIcon}</span>}
      {children}
    </button>
  );
}

function Divider({ sx = {} }) {
  return <hr style={{ border: "none", borderTop: `1px solid ${theme.border}`, margin: "0", ...sx }} />;
}

// ─── Icons (inline SVGs) ──────────────────────────────────────

const ArrowBackIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke={theme.primary} strokeWidth={2}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke={theme.textSecondary} strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

const SearchIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <circle cx="11" cy="11" r="8" />
    <path strokeLinecap="round" d="M21 21l-4.35-4.35" />
  </svg>
);

const PlaneIcon = () => (
  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke={theme.textSecondary} strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

// ─── Stepper ──────────────────────────────────────────────────

function Stepper({ steps, activeStep }: { steps: string[], activeStep: number }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0, padding: "20px 24px" }}>
      {steps.map((label, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 700,
                fontFamily: fonts.sans,
                background: i <= activeStep ? theme.primary : "#E5E7EB",
                color: i <= activeStep ? "#fff" : theme.textSecondary,
                transition: "all 0.3s ease",
              }}
            >
              {i + 1}
            </span>
            <Typography variant="body2" sx={{ color: i <= activeStep ? theme.text : theme.textSecondary, fontWeight: i <= activeStep ? 600 : 400, whiteSpace: "nowrap" }}>
              {label}
            </Typography>
          </Box>
          {i < steps.length - 1 && (
            <Box
              sx={{
                width: 48,
                height: 2,
                background: i < activeStep ? theme.primary : "#E5E7EB",
                margin: "0 12px",
                borderRadius: 1,
                transition: "background 0.3s ease",
              }}
            />
          )}
        </Box>
      ))}
    </Box>
  );
}

// ─── Date Card ────────────────────────────────────────────────

function DateCard({ label, date, isRequested }: { label: string; date: string; isRequested: boolean }) {
  return (
    <Box
      sx={{
        flex: 1,
        padding: "14px 18px",
        borderRadius: 10,
        background: isRequested ? theme.requestedBg : "#F3F4F6",
        border: isRequested ? `1.5px solid ${theme.primary}30` : "1.5px solid transparent",
        transition: "all 0.2s ease",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: isRequested ? theme.requested : theme.textSecondary,
          fontWeight: 600,
          marginBottom: 4,
          display: "block",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="subtitle1"
        sx={{
          color: isRequested ? theme.requested : theme.text,
          fontSize: 15,
          fontWeight: 700,
        }}
      >
        {date}
      </Typography>
    </Box>
  );
}

// ─── Flight Row ───────────────────────────────────────────────

function FlightRow({ flightLabel, flightCode, currentDate, requestedDate }: { flightLabel: string; flightCode: string; currentDate: string; requestedDate: string }) {
  return (
    <Box sx={{ marginBottom: 20 }}>
      <Typography
        variant="subtitle2"
        sx={{ color: theme.textSecondary, fontSize: 11, marginBottom: 10, letterSpacing: "0.08em" }}
      >
        {flightLabel} — {flightCode}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 12 }}>
        <DateCard label="Current" date={currentDate} isRequested={false} />
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          <ArrowRightIcon />
        </Box>
        <DateCard label="Requested" date={requestedDate} isRequested={true} />
      </Box>
    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────

export default function ModifyBookingWorkflow() {
  const navigate = useNavigate();
  const [activeStep] = useState(0);
  const steps = ["Received", "Check supplier", "Review quote", "Send to customer", "Customer"];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: theme.bg,
        fontFamily: fonts.sans,
        padding: "32px 0",
      }}
    >
      <Box sx={{ maxWidth: 780, margin: "0 auto", padding: "0 20px" }}>
        {/* Back Button */}
        <Button variant="outlined" startIcon={<ArrowBackIcon />} sx={{ marginBottom: 20, borderRadius: 8, padding: "8px 16px", fontSize: 13 }} onClick={() => navigate("/transaction-list")}>
          Back to transactions
        </Button>

        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <Box>
            <Typography variant="overline" sx={{ color: theme.primary, marginBottom: 6, display: "block", fontSize: 11 }}>
              MODIFY BOOKING WORKFLOW
            </Typography>
            <Typography variant="h5" component="h1" sx={{ color: theme.text }}>
              Pritam Swami — XPW-5PS-W77
            </Typography>
          </Box>
          <Chip label="Awaiting action" color="primary" sx={{ marginTop: 6 }} />
        </Box>

        {/* Stepper Card */}
        <Paper elevation={1} sx={{ marginBottom: 20, overflow: "hidden" }}>
          <Stepper steps={steps} activeStep={activeStep} />
        </Paper>

        {/* Booking Details Card */}
        <Paper elevation={1} sx={{ padding: "24px 28px", marginBottom: 20 }}>
          {/* Card Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
            <Typography variant="h6" sx={{ fontSize: 17 }}>Booking details</Typography>
            <Typography variant="caption" sx={{ color: theme.textSecondary }}>
              Mondee (MND-55129) · Booked Apr 13, 2026
            </Typography>
          </Box>

          {/* Date Change Badge */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 6 }}>
              <CalendarIcon />
              <Typography variant="subtitle1" sx={{ color: theme.primary, fontSize: 13 }}>
                Date change requested
              </Typography>
            </Box>
            <Chip label="Dates only" color="success" />
          </Box>

          {/* Flights */}
          <FlightRow
            flightLabel="OUTBOUND"
            flightCode="EMIRATES EK507"
            currentDate="Jun 24, 2026"
            requestedDate="Jun 28, 2026"
          />
          <FlightRow
            flightLabel="RETURN"
            flightCode="EMIRATES EK508"
            currentDate="Jul 04, 2026"
            requestedDate="Jul 08, 2026"
          />

          {/* Route Info Bar */}
          <Divider sx={{ margin: "6px 0 16px" }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Typography variant="body2" sx={{ color: theme.textSecondary }}>Route:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: theme.text }}>
                DEL → DXB / DXB → DEL
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Typography variant="body2" sx={{ color: theme.textSecondary }}>Cabin:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: theme.text }}>Business</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Typography variant="body2" sx={{ color: theme.textSecondary }}>Pax:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: theme.text }}>1</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Typography variant="body2" sx={{ color: theme.textSecondary }}>Current:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: theme.text }}>$4,828.35</Typography>
            </Box>
          </Box>
        </Paper>

        {/* Step 1: Check with Mondee */}
        <Paper elevation={1} sx={{ padding: "24px 28px" }}>
          <Typography variant="h6" sx={{ fontSize: 16, marginBottom: 6 }}>
            Step 1: Check with Mondee
          </Typography>
          <Typography variant="body1" sx={{ color: theme.textSecondary, marginBottom: 18 }}>
            Query Mondee for availability and pricing on the requested dates.
          </Typography>
          <Button variant="contained" startIcon={<SearchIcon />}>
            Check availability & pricing
          </Button>
        </Paper>
      </Box>
    </Box>
  );
}